import { supabase } from '@/lib/supabase/client';
import {
  defaultOutreachMode,
  deriveOperatorScope,
  planOperatorUmbrellaLinks,
  pickStrongerSegment,
  type OperatorLeadInput,
} from '@/lib/leads/operator-umbrella';
import type {
  LeadOperator,
  LeadOperatorLinkConfidence,
  LeadOperatorOutreachMode,
  LeadOperatorStoreSummary,
  LeadOperatorWithStores,
  LinkOperatorUmbrellasResult,
} from '@/types/lead-operators';
import type { LeadSegment } from '@/types/leads';
import { leadsService } from './leads';

const leadSegments = ['single_location', 'small_chain', 'regional_chain', 'mso'] as const;
const outreachModes = ['owner_to_owner', 'store_cold'] as const;
const linkConfidences = ['high', 'review'] as const;
const operatorScopes = ['single_state', 'multi_state'] as const;

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function enumOrNull<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function normalizeOperator(row: Record<string, unknown>): LeadOperator {
  return {
    id: stringOrNull(row.id) ?? '',
    display_name: stringOrNull(row.display_name) ?? 'Unnamed operator',
    operator_key: stringOrNull(row.operator_key) ?? '',
    website_domain: stringOrNull(row.website_domain),
    aliases: stringArray(row.aliases),
    segment: enumOrNull(row.segment, leadSegments),
    location_count: numberOrNull(row.location_count),
    outreach_mode: enumOrNull(row.outreach_mode, outreachModes) ?? 'owner_to_owner',
    operator_scope: enumOrNull(row.operator_scope, operatorScopes),
    created_at: stringOrNull(row.created_at) ?? '',
    updated_at: stringOrNull(row.updated_at) ?? '',
  };
}

async function listRedactedOperators(): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc('list_lead_operators_redacted');
  if (error) throw new Error(`Failed to load operators: ${error.message}`);
  return (data as Record<string, unknown>[] | null) ?? [];
}

function normalizeStore(row: Record<string, unknown>): LeadOperatorStoreSummary {
  return {
    id: stringOrNull(row.id) ?? '',
    business_name: stringOrNull(row.business_name) ?? 'Unnamed store',
    dba: stringOrNull(row.dba),
    city: stringOrNull(row.city),
    state: stringOrNull(row.state),
    segment: enumOrNull(row.segment, leadSegments),
    location_count: numberOrNull(row.location_count),
    priority_band: stringOrNull(row.priority_band),
    status: stringOrNull(row.status),
    operator_link_confidence: enumOrNull(row.operator_link_confidence, linkConfidences),
  };
}

async function refreshOperatorRollup(operatorId: string): Promise<void> {
  let rows: Record<string, unknown>[];
  try {
    rows = (await leadsService.listRedactedRecords({ includeSuppressed: true, includeDisqualified: true }))
      .filter((row) => row.operator_id === operatorId);
  } catch (error) {
    console.warn('Unable to refresh operator rollup:', error instanceof Error ? error.message : error);
    return;
  }
  const segment = rows.reduce<LeadSegment | null>((acc, row) => {
    return pickStrongerSegment(acc, enumOrNull(row.segment, leadSegments));
  }, null);
  const maxLocations = rows.reduce<number>((acc, row) => {
    const count = numberOrNull(row.location_count);
    return count != null && count > acc ? count : acc;
  }, rows.length);
  const operatorScope = deriveOperatorScope(rows.map((row) => stringOrNull(row.state)));

  const { error } = await supabase.rpc('update_lead_operator_redacted', {
    p_operator_id: operatorId,
    p_changes: {
      segment,
      location_count: Math.max(rows.length, maxLocations),
      outreach_mode: defaultOutreachMode(segment),
      operator_scope: operatorScope,
    },
  });
  if (error) throw new Error(`Unable to refresh operator rollup: ${error.message}`);
}

export const leadOperatorsService = {
  async listOwnerOperatorIds(): Promise<Set<string>> {
    try {
      const data = await listRedactedOperators();
      return new Set(
        data
          .filter((row) => row.outreach_mode === 'owner_to_owner')
          .map((row) => stringOrNull(row.id))
          .filter((id): id is string => Boolean(id))
      );
    } catch (error) {
      console.warn('Unable to load owner operator ids:', error instanceof Error ? error.message : error);
      return new Set();
    }
  },

  async listWithStores(): Promise<LeadOperatorWithStores[]> {
    const operatorsData = await listRedactedOperators();

    const operators = ((operatorsData as Record<string, unknown>[]) ?? []).map(normalizeOperator);
    if (operators.length === 0) return [];

    const operatorIds = new Set(operators.map((operator) => operator.id));
    const storesData = (await leadsService.listRedactedRecords({ includeSuppressed: true, includeDisqualified: true }))
      .filter((row) => typeof row.operator_id === 'string' && operatorIds.has(row.operator_id));

    const storesByOperator = new Map<string, LeadOperatorStoreSummary[]>();
    for (const row of storesData) {
      const operatorId = stringOrNull(row.operator_id);
      if (!operatorId) continue;
      const list = storesByOperator.get(operatorId) ?? [];
      list.push(normalizeStore(row));
      storesByOperator.set(operatorId, list);
    }

    return operators.map((operator) => ({
      ...operator,
      stores: storesByOperator.get(operator.id) ?? [],
    }));
  },

  /**
   * Cluster + attach multi-location leads under operator umbrellas.
   * Safe to re-run; already-linked leads are skipped.
   * Pass forceLeadIds (Browse selection) to move those rows even if they look like singles.
   */
  async linkUmbrellas(forceLeadIds: string[] = []): Promise<LinkOperatorUmbrellasResult> {
    const [operatorsData, leadsData] = await Promise.all([
      listRedactedOperators(),
      leadsService.listRedactedRecords({ includeSuppressed: false, includeDisqualified: false }),
    ]);

    const existingOperators = ((operatorsData as Record<string, unknown>[]) ?? []).map(normalizeOperator);
    const leadInputs: OperatorLeadInput[] = leadsData.map((row) => ({
      id: stringOrNull(row.id) ?? '',
      business_name: stringOrNull(row.business_name) ?? '',
      dba: stringOrNull(row.dba),
      website: stringOrNull(row.website),
      email: stringOrNull(row.email),
      segment: enumOrNull(row.segment, leadSegments),
      location_count: numberOrNull(row.location_count),
      operator_id: stringOrNull(row.operator_id),
      operator_link_method:
        row.operator_link_method === 'human_selected' ||
        row.operator_link_method === 'verified_domain' ||
        row.operator_link_method === 'curated_alias'
          ? row.operator_link_method
          : 'legacy_unverified',
      city: stringOrNull(row.city),
      state: stringOrNull(row.state),
    }));

    const forceSet = new Set(forceLeadIds.filter(Boolean));
    const leadById = new Map(leadInputs.map((lead) => [lead.id, lead]));
    const selectedAlreadyLinked = [...forceSet].filter((id) => Boolean(leadById.get(id)?.operator_id)).length;
    const selectedNotFound = [...forceSet].filter((id) => !leadById.has(id)).length;
    const alreadyLinkedMultiLoc = leadInputs.filter(
      (lead) =>
        Boolean(lead.operator_id) &&
        ((lead.location_count != null && lead.location_count >= 2) ||
          lead.segment === 'small_chain' ||
          lead.segment === 'regional_chain' ||
          lead.segment === 'mso' ||
          forceSet.has(lead.id))
    ).length;

    const plan = planOperatorUmbrellaLinks(leadInputs, existingOperators, {
      forceLeadIds: forceSet,
    });
    const uniqueAttachIds = new Set(plan.attaches.map((a) => a.leadId));

    const tempIdToRealId = new Map<string, string>();
    let operatorsCreated = 0;
    let operatorsUpdated = 0;
    let leadsAttached = 0;
    let reviewLinks = 0;

    for (const create of plan.creates) {
      const { data, error } = await supabase.rpc('upsert_lead_operator_redacted', {
        p_display_name: create.display_name, p_operator_key: create.operator_key,
        p_website_domain: create.website_domain, p_aliases: create.aliases,
        p_segment: create.segment, p_outreach_mode: create.outreach_mode,
        p_location_count: create.leadIds.length,
      });

      if (error) throw new Error(`Failed to create operator ${create.operator_key}: ${error.message}`);
      const operator = normalizeOperator(data as Record<string, unknown>);
      tempIdToRealId.set(create.tempId, operator.id);
      const existed = existingOperators.some((row) => row.operator_key === create.operator_key);
      if (existed) operatorsUpdated += 1;
      else operatorsCreated += 1;
    }

    const touchedOperatorIds = new Set<string>();

    for (const attach of plan.attaches) {
      const operatorId = tempIdToRealId.get(attach.operatorId) ?? attach.operatorId;
      const confidence: LeadOperatorLinkConfidence = attach.confidence;
      if (confidence === 'review') reviewLinks += 1;

      const { data, error } = await supabase.rpc('set_lead_operator_link', {
        p_lead_ids: [attach.leadId],
        p_operator_id: operatorId,
      });

      if (error) throw new Error(`Failed to attach lead ${attach.leadId}: ${error.message}`);
      if ((data as number | null) && data > 0) {
        leadsAttached += 1;
        touchedOperatorIds.add(operatorId);
      }
    }

    for (const operatorId of touchedOperatorIds) {
      await refreshOperatorRollup(operatorId);
      operatorsUpdated += 1;
    }

    return {
      operatorsCreated,
      operatorsUpdated,
      leadsAttached,
      leadsSkipped: plan.skippedLeadIds.length,
      reviewLinks,
      candidatesConsidered: uniqueAttachIds.size,
      alreadyLinkedMultiLoc,
      selectedAlreadyLinked,
      selectedNotFound,
    };
  },

  async setOutreachMode(operatorId: string, outreachMode: LeadOperatorOutreachMode): Promise<void> {
    const { error } = await supabase.rpc('update_lead_operator_redacted', {
      p_operator_id: operatorId,
      p_changes: { outreach_mode: outreachMode },
    });

    if (error) throw new Error(`Failed to update outreach mode: ${error.message}`);
  },
};
